// @flow
import React, {FunctionComponent} from 'react';
import {Control, FieldErrors, UseFormRegister, UseFormWatch} from "react-hook-form";
import {Button, FormControlLabel, IconButton, Stack, Switch, TextField} from "@mui/material";
import FlexStack from "../Common/flex-stack";
import PersonRemoveIcon from "@mui/icons-material/PersonRemove";
import {InfoBox} from "../Common/alerts";
import {Info} from "@mui/icons-material";

export const requiredFieldConfig = {required: {value: true, message: "Pflichtfeld"}};

interface SectionProps {
    register: UseFormRegister<any>;
    errors: FieldErrors<any>;
    watch?: UseFormWatch<any>;
    control?: Control<any>;
}

export const FarmDetailsFields: FunctionComponent<SectionProps> = ({register, errors, watch}) => {
    const inTbcProgram = watch ? watch('inTbcProgram') : false;

    return (
        <FlexStack>
            <TextField
                {...register('farmName', requiredFieldConfig)}
                label="Hofname"
                required
                error={!!errors.farmName}
                helperText={errors.farmName?.message as string ?? ''}/>
            <TextField
                {...register('herdCode')}
                label="Herdkürzel"
                error={!!errors.herdCode}
                helperText={errors.herdCode?.message as string ?? ''}/>
            <TextField label="Webseite" {...register("website")}/>
            <TextField label="Telefonnummer" {...register("phone")}/>
            <TextField label="Steuernummer" {...register("vatNumber")}/>
            <TextField label="Hofnummer" {...register("farmNumber")}/>
            <FormControlLabel control={<Switch checked={!!inTbcProgram} {...register('inTbcProgram')}/>}
                              label={
                                  <Stack direction="row" gap={1}>
                                      TBC-Programm
                                      {inTbcProgram ? (
                                          <Stack direction="row" gap={0.5}>
                                              <Info/>
                                              Mein Hof nimmt an einem TBC-Testprogramm teil und gilt als TBC frei
                                          </Stack>
                                      ) : ''}
                                  </Stack>
                              }/>
        </FlexStack>
    );
};

export const AddressFields: FunctionComponent<SectionProps> = ({register, errors}) => {
    const addressErrors = errors.address as FieldErrors<any> | undefined;
    return (
        <FlexStack>
            <TextField
                {...register('address.country', requiredFieldConfig)}
                label="Land"
                required
                error={!!addressErrors?.country}
                helperText={addressErrors?.country?.message as string ?? ''}/>
            <TextField
                {...register('address.state', requiredFieldConfig)}
                label="Bundesland"
                required
                error={!!addressErrors?.state}
                helperText={addressErrors?.state?.message as string ?? ''}/>
            <TextField
                {...register('address.zipCode', requiredFieldConfig)}
                label="PLZ"
                required
                error={!!addressErrors?.zipCode}
                helperText={addressErrors?.zipCode?.message as string ?? ''}/>
            <TextField
                {...register('address.city', requiredFieldConfig)}
                label="Stadt"
                required
                error={!!addressErrors?.city}
                helperText={addressErrors?.city?.message as string ?? ''}/>
            <TextField
                {...register('address.street', requiredFieldConfig)}
                label="Straße und Hausnummer"
                required
                error={!!addressErrors?.street}
                helperText={addressErrors?.street?.message as string ?? ''}/>
        </FlexStack>
    );
};

export const OwnerFields: FunctionComponent<SectionProps & { addOwner: () => void, removeOwner: (index: number) => void, fields: any[] }> = ({
                                                                                                                                              register,
                                                                                                                                              errors,
                                                                                                                                              fields,
                                                                                                                                              addOwner,
                                                                                                                                              removeOwner
                                                                                                                                          }) => {
    const ownersErrors = errors.owners as FieldErrors<any>[] | undefined;
    return (
        <FlexStack>
            {fields.map((field, index) => (
                <Stack key={field.id} direction="row" alignItems="baseline" gap={2}>
                    <TextField
                        fullWidth
                        {...register(`owners.${index}.name`, requiredFieldConfig)}
                        label="Vorname, Nachname"
                        required
                        error={!!ownersErrors?.[index]?.name}
                        helperText={ownersErrors?.[index]?.name?.message as string ?? ''}/>
                    <IconButton color="primary"
                                size="small"
                                aria-label="remove owner"
                                onClick={() => removeOwner(index)}>
                        <PersonRemoveIcon/>
                    </IconButton>
                </Stack>
            ))}
            {!fields.length && (
                <InfoBox>
                    Keine Besitzer hinterlegt. <Button onClick={addOwner}>Besitzer hinzufügen</Button>.
                </InfoBox>
            )}
        </FlexStack>
    );
};
